/**
 * Every guide on /guides: the ones written as data (articles/<slug>.js, shown
 * by GuideArticle), and the older hand-built pages, so the hub and
 * "Keep reading" can list them all. Plain data: the app, the pre-renderer and
 * src/content/seo/pages.js all read it.
 *
 * A new data guide: add articles/<slug>.js (metadata) and bodies/<slug>.js
 * (the article), then list it in the imports and DATA_GUIDES here and in
 * bodies/loaders.js and bodies/all.js (guides.test.js checks they agree).
 */
import { GUIDE_CATEGORIES, guidePath } from './format.js';
import airlineFareHoldPolicies from './articles/airline-fare-hold-policies.js';
import airlinesThatOfferBuyNowPayLater from './articles/airlines-that-offer-buy-now-pay-later.js';
import basicEconomyRealCost from './articles/basic-economy-real-cost.js';
import biggestTravelSitesSlowestFlightSearch from './articles/biggest-travel-sites-slowest-flight-search.js';
import bookFlightsWithBadCredit from './articles/book-flights-with-bad-credit.js';
import bookFlightsWithoutKlarnaOrAffirm from './articles/book-flights-without-klarna-or-affirm.js';
import cheapestDayToFly from './articles/cheapest-day-to-fly.js';
import doCheapFlightSitesHaveCheaperFares from './articles/do-cheap-flight-sites-have-cheaper-fares.js';
import doesBuyNowPayLaterAffectCreditScore from './articles/does-buy-now-pay-later-affect-credit-score.js';
import flightLoansVsPaymentPlans from './articles/flight-loans-vs-payment-plans.js';
import flightPaymentPlansNoCreditCheck from './articles/flight-payment-plans-no-credit-check.js';
import holidayFlightsPayOverTime from './articles/holiday-flights-pay-over-time.js';
import howToSaveForATrip from './articles/how-to-save-for-a-trip.js';
import internationalFlightsPaymentPlan from './articles/international-flights-payment-plan.js';
import isFlyNowPayLaterLegit from './articles/is-fly-now-pay-later-legit.js';
import missedPayLaterPaymentOnAFlight from './articles/missed-pay-later-payment-on-a-flight.js';
import payForFlightsWithDebitCard from './articles/pay-for-flights-with-debit-card.js';
import payMonthlyForFlights from './articles/pay-monthly-for-flights.js';
import studentFlightsNoCreditHistory from './articles/student-flights-no-credit-history.js';
import whenToBookFlightsFareData from './articles/when-to-book-flights-fare-data.js';

/** Data guides, newest first; same-day guides keep the order listed here (the hub's order). */
export const DATA_GUIDES = Object.freeze([
  doCheapFlightSitesHaveCheaperFares,
  biggestTravelSitesSlowestFlightSearch,
  whenToBookFlightsFareData,
  cheapestDayToFly,
  basicEconomyRealCost,
  bookFlightsWithBadCredit,
  bookFlightsWithoutKlarnaOrAffirm,
  flightPaymentPlansNoCreditCheck,
  airlinesThatOfferBuyNowPayLater,
  doesBuyNowPayLaterAffectCreditScore,
  flightLoansVsPaymentPlans,
  payMonthlyForFlights,
  missedPayLaterPaymentOnAFlight,
  payForFlightsWithDebitCard,
  internationalFlightsPaymentPlan,
  studentFlightsNoCreditHistory,
  holidayFlightsPayOverTime,
  airlineFareHoldPolicies,
  howToSaveForATrip,
  isFlyNowPayLaterLegit,
].sort((a, b) => (a.published === b.published ? 0 : a.published < b.published ? 1 : -1)));

/**
 * The hand-built guide pages (src/pages/articles), with the hub card each one
 * shows. `section` is the hub section it's listed under.
 */
export const PAGE_GUIDES = Object.freeze([
  {
    slug: 'fastest-flight-search-engines',
    section: 'Research',
    label: 'Benchmark',
    cardTitle: 'Which Flight Search Engines Load the Fastest? (2026)',
    cardDescription: 'We timed 19 flight search engines, from Google Flights and Kayak to Expedia and Priceline, on six routes to see which show flight results fastest.',
  },
  {
    slug: 'flight-installments-bnpl-payment-options',
    section: 'Pay over time',
    label: 'Payment Methods',
    cardTitle: 'Best Ways to Book Flights on Installments Without Credit Cards',
    cardDescription: 'Learn how to pay for airline tickets with BNPL apps, debit cards, PayPal installments, and flight-specific payment plans, with no credit card needed.',
  },
  {
    slug: 'flight-layaway-vs-bnpl',
    section: 'Pay over time',
    label: 'Decision Guide',
    cardTitle: 'Flight Layaway vs BNPL: What’s the Difference?',
    cardDescription: 'Compare layaway vs BNPL for flights: when you pay, fees, credit checks, and which option fits your travel timeline.',
  },
  {
    slug: 'defera-vs-affirm-klarna-uplift',
    section: 'Pay over time',
    label: 'Comparison',
    cardTitle: 'Defera vs Affirm vs Klarna vs Uplift: BNPL Comparison',
    cardDescription: 'Compare Defera to Affirm, Klarna, Uplift and other buy now, pay later providers for flights: credit checks, interest, schedules and what you can book.',
  },
  {
    slug: 'fintech-installment-flight-booking',
    section: 'Pay over time',
    label: 'Payment Options',
    cardTitle: 'Best Fintech Options for Booking Flights on Installments',
    cardDescription: 'Compare top fintech platforms and airline plans for installment flight payments, including how to secure fares with deposits and manage flexible schedules.',
  },
  {
    slug: 'lock-flight-price-with-deposit',
    section: 'Booking tips',
    label: 'Price Locks',
    cardTitle: 'How to Lock in Flight Prices With a Deposit (Fare Hold vs Price Freeze vs Layaway)',
    cardDescription: 'Learn deposit and price-lock options, step-by-step checklists, and how to avoid hidden fees and surprises before you travel.',
  },
  {
    slug: 'pay-flights-with-gift-cards-credits',
    section: 'Booking tips',
    label: 'Gift Cards & Credits',
    cardTitle: 'How to Pay for Flights With Airline Gift Cards & Travel Credits',
    cardDescription: 'How to use airline gift cards and travel credits for flight bookings, which pay-later providers accept them, and how Defera handles them.',
  },
  {
    slug: 'semi-private-flights-bnpl',
    section: 'Booking tips',
    label: 'Semi-Private',
    cardTitle: 'Book Semi-Private Flights on Installments',
    cardDescription: 'Why fly semi-private, which buy now, pay later providers support semi-private flights, and how to book a semi-private flight and pay over time.',
  },
]);

/** The data guide with this slug, or null. */
export const guideMeta = (slug) => DATA_GUIDES.find((g) => g.slug === slug) || null;

/** A guide's hub card ({ to, section, category, title, description }), for any guide, or null. */
export function guideCard(slug) {
  const g = guideMeta(slug);
  if (g) return { to: guidePath(g.slug), section: g.category, category: g.label || g.category, title: g.title, description: g.description };
  const p = PAGE_GUIDES.find((x) => x.slug === slug);
  if (p) return { to: guidePath(p.slug), section: p.section, category: p.label, title: p.cardTitle, description: p.cardDescription };
  return null;
}

/** Every guide slug the hub lists. */
export const ALL_GUIDE_SLUGS = Object.freeze([...DATA_GUIDES.map((g) => g.slug), ...PAGE_GUIDES.map((p) => p.slug)]);

/** The hub: [{ section, cards }] in GUIDE_CATEGORIES order, data guides first in each. */
export function hubSections() {
  return GUIDE_CATEGORIES.map((section) => ({
    section,
    cards: [
      ...DATA_GUIDES.filter((g) => g.category === section).map((g) => guideCard(g.slug)),
      ...PAGE_GUIDES.filter((p) => p.section === section).map((p) => guideCard(p.slug)),
    ],
  })).filter((s) => s.cards.length);
}
